/**
 * Single source of the `[CLAWCRAFT_TRIGGER]` wire format — the signal
 * envelopes Convex POSTs to the per-user pod's channel webhook (42618).
 * Owns the whole envelope family (precedent: `media-upload-block.ts`):
 *
 *   - run / validate — button-issued spec directives (migrated verbatim
 *     from webhookDispatch.ts; byte-identical output pinned by frozen
 *     fixtures in `test/unit/domain/intent-relay.test.ts`)
 *   - complete-task / capture-task / resolve-bead — the intent-relay
 *     family (task-surface write channel; claw-doctrine §5.6.1)
 *
 * Grammar is LINE-ORIENTED: one `Key: value` per line between the
 * markers. Free-text payloads (CaptureText/Answer) are therefore
 * sanitized by collapsing ALL whitespace runs (incl. CR/LF) to single
 * spaces — that alone kills line injection — plus literal marker-tag
 * stripping and a 500-char cap. Payloads are NOT shell-pre-escaped:
 * that contract is `TriggerPayload:`-only (the agent interpolates it
 * verbatim); for capture/answer the agent mediates and composes its own
 * command, treating the text strictly as data. JSON-encoding the
 * payload was REJECTED (agent readability; newline-collapse suffices).
 *
 * Byte budgets (binding guard — char caps are UX affordances):
 * complete/run/validate envelopes stay under the 300-byte scaffold pin
 * (bounding orchestration prose, not user content); capture/resolve
 * carry user text and are bounded at 1024 bytes total.
 *
 * Pure module: imports `domain/shared/*` ONLY — no praxis (not even the
 * beads gateway), no Convex, no React. Convex-bundle-safe by
 * construction. `crypto.randomUUID()` stays Convex-side; this module
 * only formats/validates the id.
 */

import { err, ok, type Result } from "./shared/result";
import type { Brand } from "./shared/types";

// ─── Markers + intent catalogue ──────────────────────────────────────

export const TRIGGER_OPEN = "[CLAWCRAFT_TRIGGER]";
export const TRIGGER_CLOSE = "[/CLAWCRAFT_TRIGGER]";

/**
 * Full catalogue of `Intent:` values on the channel-webhook envelope
 * grammar. `resume-execution` is composed pod-side (self-addressed cron
 * envelope, praxis-doctrine §10.5) — listed for catalogue completeness;
 * Convex never composes it.
 */
export const TRIGGER_INTENTS = [
  "run",
  "validate",
  "resume-execution",
  "complete-task",
  "capture-task",
  "resolve-bead",
  "pause-workflow",
  "activate-workflow",
] as const;
export type TriggerIntent = (typeof TRIGGER_INTENTS)[number];

/**
 * The intents dispatched through the `intent_relays` channel: the three
 * task-surface intents plus the two workflow-control intents
 * (workflow-pause-toggle spec). All ride the deterministic executor
 * first with the envelope path as fallback.
 */
export const RELAY_INTENTS = [
  "complete-task",
  "capture-task",
  "resolve-bead",
  "pause-workflow",
  "activate-workflow",
] as const;
export type RelayIntent = (typeof RELAY_INTENTS)[number];

/** The two spec-targeted relay intents (thread-addressed, no bead). */
export const SPEC_RELAY_INTENTS = [
  "pause-workflow",
  "activate-workflow",
] as const;
export type SpecRelayIntent = (typeof SPEC_RELAY_INTENTS)[number];

export function isSpecRelayIntent(
  intent: RelayIntent,
): intent is SpecRelayIntent {
  return intent === "pause-workflow" || intent === "activate-workflow";
}

// ─── Caps + byte budgets ─────────────────────────────────────────────

export const MAX_CAPTURE_TEXT_CHARS = 500;
export const MAX_DECISION_ANSWER_CHARS = 500;

/**
 * Scaffold pin for user-content-free envelopes (complete/run/validate)
 * AND the fixed scaffold of the free-text envelopes: orchestration
 * prose stays bounded regardless of payload.
 */
export const TRIGGER_SCAFFOLD_MAX_BYTES = 300;

/** Total budget for envelopes carrying sanitized user text. */
export const MAX_RELAY_ENVELOPE_BYTES = 1024;

// ─── RelayId brand ───────────────────────────────────────────────────

export type IntentRelayId = Brand<string, "IntentRelayId">;

const RELAY_ID_RE =
  /^relay_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** `relay_<uuid>` — the uuid is minted by the caller (Convex-side). */
export function formatIntentRelayId({ uuid }: { uuid: string }): IntentRelayId {
  return `relay_${uuid}` as IntentRelayId;
}

export function isIntentRelayId(value: string): value is IntentRelayId {
  return RELAY_ID_RE.test(value);
}

// ─── Sanitizer ───────────────────────────────────────────────────────

const MARKER_TAG_RE =
  /\[\/?CLAWCRAFT_TRIGGER\]|\[\/?CLAWCRAFT_DIRECTIVE\]/g;

/**
 * Normalize free text for a line-oriented envelope: strip literal
 * marker tags (open + close, TRIGGER + retired DIRECTIVE) to fixpoint
 * (a single pass could REASSEMBLE a tag from its severed halves),
 * collapse every whitespace run (incl. CR/LF) to a single space, trim.
 * No cap — callers decide whether to refuse (`text_too_long`) or
 * truncate.
 */
function normalizeEnvelopeText(text: string): string {
  let stripped = text;
  let before: string;
  do {
    before = stripped;
    stripped = stripped.replace(MARKER_TAG_RE, "");
  } while (stripped !== before);
  return stripped.replace(/\s+/g, " ").trim();
}

/**
 * The exported sanitizer: normalize + hard cap at 500 chars. Idempotent
 * (unit-pinned). Composers refuse over-cap input instead of silently
 * truncating; this capped form is the defensive backstop for storage.
 */
export function sanitizeEnvelopeText(text: string): string {
  return normalizeEnvelopeText(text).slice(0, MAX_CAPTURE_TEXT_CHARS).trim();
}

// ─── Shell escape (TriggerPayload-only contract) ─────────────────────

/**
 * POSIX single-quote escape idiom (`'` → `'\''`) so the agent can
 * interpolate the line verbatim onto a `--trigger-payload '<json>'`
 * argument. Applies to `TriggerPayload:` ONLY — never to
 * CaptureText/Answer (the agent mediates those).
 */
export function escapeShellSingleQuotes(text: string): string {
  return text.replace(/'/g, "'\\''");
}

// ─── Act line (relay envelopes only) ─────────────────────────────────

/**
 * In-envelope imperative appended to the three relay envelopes (never
 * to the byte-frozen run/validate directives). Live incident 2026-07-15
 * (claw-doctrine §5.6.1 v2.42.2): on metadata-only envelopes the pod
 * model took the no-reply path without consulting the intent map — the
 * message content itself must carry the ask, because prose in AGENTS.md
 * is not reliably consulted for machine-shaped notifications.
 */
export const TRIGGER_ACT_LINE =
  "Act: run the mapped verb for this intent now — nothing happens without it. Reply one line echoing RelayId.";

// ─── Errors ──────────────────────────────────────────────────────────

export type IntentRelayError =
  | { readonly kind: "empty_text" }
  | { readonly kind: "text_too_long"; readonly maxChars: number }
  | { readonly kind: "envelope_too_large"; readonly maxBytes: number };

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

// ─── Composers: intent-relay family ──────────────────────────────────

/**
 * Complete-task envelope (Source: task-check). Total function — shortId
 * and RelayId are structurally bounded, so the envelope stays under the
 * 300-byte scaffold pin by construction (test-asserted at max input).
 */
export function composeCompleteTaskTrigger({
  beadShortId,
  relayId,
}: {
  beadShortId: string;
  relayId: IntentRelayId;
}): string {
  return [
    TRIGGER_OPEN,
    "Source: task-check",
    "Intent: complete-task",
    `BeadId: ${beadShortId}`,
    `RelayId: ${relayId}`,
    TRIGGER_ACT_LINE,
    TRIGGER_CLOSE,
  ].join("\n");
}

/**
 * Capture-task envelope (Source: task-capture). CaptureText is the
 * user's raw capture, sanitized — deliberately routed to the agent so
 * the worth-being-a-bead filter stays agent-applied.
 */
export function composeCaptureTaskTrigger({
  captureText,
  relayId,
}: {
  captureText: string;
  relayId: IntentRelayId;
}): Result<string, IntentRelayError> {
  const text = normalizeEnvelopeText(captureText);
  if (text.length === 0) return err({ kind: "empty_text" });
  if (text.length > MAX_CAPTURE_TEXT_CHARS) {
    return err({ kind: "text_too_long", maxChars: MAX_CAPTURE_TEXT_CHARS });
  }
  const envelope = [
    TRIGGER_OPEN,
    "Source: task-capture",
    "Intent: capture-task",
    `RelayId: ${relayId}`,
    `CaptureText: ${text}`,
    TRIGGER_ACT_LINE,
    TRIGGER_CLOSE,
  ].join("\n");
  if (byteLength(envelope) > MAX_RELAY_ENVELOPE_BYTES) {
    return err({
      kind: "envelope_too_large",
      maxBytes: MAX_RELAY_ENVELOPE_BYTES,
    });
  }
  return ok(envelope);
}

/**
 * Resolve-bead envelope (Source: decision-card). Answer is the user's
 * free-text decision answer, sanitized; the agent composes a non-empty
 * output object from it (intent-map contract).
 */
export function composeResolveBeadTrigger({
  beadShortId,
  answer,
  relayId,
}: {
  beadShortId: string;
  answer: string;
  relayId: IntentRelayId;
}): Result<string, IntentRelayError> {
  const text = normalizeEnvelopeText(answer);
  if (text.length === 0) return err({ kind: "empty_text" });
  if (text.length > MAX_DECISION_ANSWER_CHARS) {
    return err({ kind: "text_too_long", maxChars: MAX_DECISION_ANSWER_CHARS });
  }
  const envelope = [
    TRIGGER_OPEN,
    "Source: decision-card",
    "Intent: resolve-bead",
    `BeadId: ${beadShortId}`,
    `RelayId: ${relayId}`,
    `Answer: ${text}`,
    TRIGGER_ACT_LINE,
    TRIGGER_CLOSE,
  ].join("\n");
  if (byteLength(envelope) > MAX_RELAY_ENVELOPE_BYTES) {
    return err({
      kind: "envelope_too_large",
      maxBytes: MAX_RELAY_ENVELOPE_BYTES,
    });
  }
  return ok(envelope);
}

/**
 * Pause-workflow envelope (Source: workflow-control). Total function —
 * ThreadId and RelayId are structurally bounded, so the envelope stays
 * under the 300-byte scaffold pin by construction (test-asserted).
 * Fallback path only: the deterministic executor's `pause-workflow`
 * intent is primary (workflow-pause-toggle spec).
 */
export function composePauseWorkflowTrigger({
  threadId,
  relayId,
}: {
  threadId: string;
  relayId: IntentRelayId;
}): string {
  return [
    TRIGGER_OPEN,
    "Source: workflow-control",
    "Intent: pause-workflow",
    `ThreadId: ${threadId}`,
    `RelayId: ${relayId}`,
    TRIGGER_ACT_LINE,
    TRIGGER_CLOSE,
  ].join("\n");
}

/**
 * Activate-workflow (resume) envelope (Source: workflow-control). Same
 * structural bounds as {@link composePauseWorkflowTrigger}.
 */
export function composeActivateWorkflowTrigger({
  threadId,
  relayId,
}: {
  threadId: string;
  relayId: IntentRelayId;
}): string {
  return [
    TRIGGER_OPEN,
    "Source: workflow-control",
    "Intent: activate-workflow",
    `ThreadId: ${threadId}`,
    `RelayId: ${relayId}`,
    TRIGGER_ACT_LINE,
    TRIGGER_CLOSE,
  ].join("\n");
}

// ─── Composers: migrated spec directives (byte-frozen) ───────────────

/**
 * Run-button signal envelope. Tells the agent "the user clicked RUN on
 * thread <id> with this trigger payload"; the agent routes through
 * AGENTS.md's intent map (`praxis spec activate-execution <threadId>
 * --trigger-payload '<json>'`) — same handler as user typing "run" in
 * chat. Multi-phase walk-loop coaching, factory body embedding, and
 * per-bead update prose all live in praxis-doctrine §10.5 + the agent's
 * own discovery via `praxis manifest show` — not in this envelope.
 *
 * `triggerPayload` is opaque (spec-author-defined) — JSON-stringified
 * and shell-quote-escaped here (`escapeShellSingleQuotes`) so it
 * survives the agent's shell tool's single-quote parsing once
 * interpolated onto a `--trigger-payload '<json>'` argument.
 *
 * Envelope stays under a few hundred bytes — regression assertions in
 * webhookDispatch.test.ts pin it at < 300 bytes (down from ~2000+).
 * Output is byte-identical to the pre-migration inline composer
 * (frozen-fixture pinned).
 */
export function composeExecutionDirective({
  threadId,
  triggerPayload,
}: {
  threadId: string;
  triggerPayload: unknown;
}): string {
  const triggerPayloadJson = escapeShellSingleQuotes(
    JSON.stringify(triggerPayload),
  );
  return [
    TRIGGER_OPEN,
    "Source: run-button",
    "Intent: run",
    `ThreadId: ${threadId}`,
    `TriggerPayload: ${triggerPayloadJson}`,
    TRIGGER_CLOSE,
  ].join("\n");
}

// `composeValidateDirective` (validate-button envelope) was RETIRED by the
// deterministic-validate spec — the Check-it click now dispatches the
// `validate-spec` intent to the executor (webhookDispatch.sendValidationIntent);
// no agent turn, no envelope. See claw-doctrine §5.6.1.
